import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';

interface TextSizePlugin {
  getScale(): Promise<{ scale: number }>;
  addListener(event: 'textScaleChanged', listener: (value: { scale: number }) => void): Promise<PluginListenerHandle>;
}

const TextSize = registerPlugin<TextSizePlugin>('RubikooTextSize');
const MAX_TEXT_SCALE = 1.35;

/** Text-only scaling: do not enlarge viewport, safe areas, icons or keyboard offsets. */
export function applyTextScale(scale: number) {
  if (!Number.isFinite(scale) || scale <= 0) return;
  // Bound the current compact UI until full accessibility-size layouts exist.
  // A standard iOS setting (scale 1) must retain the original typography exactly.
  const appliedScale = Math.min(scale, MAX_TEXT_SCALE);
  document.documentElement.style.setProperty('--app-text-scale', String(appliedScale));
  document.documentElement.dataset.largeText = String(appliedScale > 1.2);
}

export async function startNativeTextSizing(): Promise<() => Promise<void>> {
  // Android WebView/browser preference handling must not be double-scaled.
  if (Capacitor.getPlatform() !== 'ios') return async () => {};
  let handle: PluginListenerHandle | undefined;
  try {
    handle = await TextSize.addListener('textScaleChanged', value => applyTextScale(value.scale));
  } catch { /* An older native shell can still render at its existing size. */ }
  try {
    const value = await TextSize.getScale();
    applyTextScale(value.scale);
  } catch { /* Do not block sign-in/navigation if the native bridge is unavailable. */ }
  return async () => { await handle?.remove(); };
}
